import React, { useState, useCallback } from 'react'
import { Formik } from 'formik'
import * as yup from 'yup'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { Button } from 'blaze-ui/Button'
import { Input } from 'almanac-ui/TextInputField/Input'
import { UserClient } from 'api/clients/UserClient'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import { RegistrationStepProps } from 'screens/Registration/types'
import useURLParams from 'hooks/useURLParams'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import Styles from './CreateAccount.module.scss'
import { splitUserName, trackCreateAccountComplete, trackCreateAccountFailed } from 'screens/LoginOrSignup/utils'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'
import Close from 'blaze-ui/icons/20/Close'
import { Check } from 'blaze-ui/icons/16'
import { useSignUp } from 'screens/Registration/hooks/useSignUp'
import { authFromResponse } from 'utils/authFromResponse'
import { generatePath } from 'react-router-dom'
import { BLAZE_HOME_PATH } from 'paths'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { useHistory } from 'react-router-dom'
import { WhiteLabelLogo } from 'components/WhiteLabelLogo/WhiteLabelLogo'
import { useInvitedWorkspace } from 'hooks/useInvitedWorkspace'

const initialValues = {
  name: '',
  password: '',
}

const validationSchema = yup.object().shape({
  name: yup.string().required('Full name is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .matches(/\d+/, 'Password must contain a number')
    .matches(/[A-Z]+/, 'Password must contain an uppercase letter'),
})

export const CreateAccount: React.FC<RegistrationStepProps> = ({ onNext }) => {
  const history = useHistory()
  const [searchParams, setSearchParams] = useURLParams({ pushNewState: false })
  const email = searchParams.get('email') || ''
  const token = searchParams.get('token') || undefined
  const emailVerificationCode = searchParams.get('emailVerificationCode') || undefined
  const workspaceInviteToken =
    searchParams.get('workspaceInviteToken') || searchParams.get('workspace_invite_token') || undefined
  const redirectTo = searchParams.get('redirectTo') || undefined
  const { workspace: invitedWorkspace } = useInvitedWorkspace(workspaceInviteToken)
  const [apiError, setApiError] = useState('')
  const { onSignUpSuccess } = useSignUp()
  const { trackAccountDetailsSubmitted, trackSignupMethodSelected } = useOnboardingTracking()

  const handleSubmit = useCallback(
    async (values: { name: string; password: string }, { setSubmitting, resetForm }: any) => {
      setApiError('')

      const { firstName, lastName } = splitUserName(values.name)
      if (!firstName) {
        setApiError('Please enter your full name.')
        setSubmitting(false)
        return
      }

      try {
        // 1) Register user
        const res = await UserClient.registerUser(
          firstName,
          lastName,
          values.password,
          token,
          emailVerificationCode,
          email,
          workspaceInviteToken,
          redirectTo,
        )

        if (res.type !== SUCCESS_RESPONSE_TYPE || !res.data.success) {
          setApiError('An error occurred. Please try again.')
          trackCreateAccountFailed(res.data.error)
          setSubmitting(false)
          return
        }

        trackCreateAccountComplete()

        // 2) Build auth from response
        const auth = authFromResponse(res.data.user, res.headers)

        // 3) onSignUpSuccess -> returns the 'landingWorkspace'
        const { landingWorkspace, isLandingWorkspaceDefaultWorkspace } = await onSignUpSuccess(
          auth,
          workspaceInviteToken,
        )
        trackAccountDetailsSubmitted()

        trackSignupMethodSelected('email')

        // 4) For future Payment step, store that workspace in the query param or machine context
        // If the landing workspace is the default workspace, send user to the existing workspace
        if (landingWorkspace && isLandingWorkspaceDefaultWorkspace) {
          const redirectToPath = redirectTo || generatePath(BLAZE_HOME_PATH, { workspaceId: landingWorkspace.id })
          setSearchParams({
            redirectTo: redirectToPath,
          })
        }

        // If the landing workspace is not the default workspace, send user to the new workspace
        if (landingWorkspace && !isLandingWorkspaceDefaultWorkspace) {
          const path = generatePath(BLAZE_HOME_PATH, { workspaceId: landingWorkspace.id })
          history.push(path)
          return
        }

        // 5) XState: go to next step => "customerProfile.mobileProfile.businessType"
        resetForm()
        onNext()
      } catch (err) {
        setApiError('An error occurred. Please try again.')
      } finally {
        setSubmitting(false)
      }
    },
    [
      token,
      emailVerificationCode,
      email,
      workspaceInviteToken,
      redirectTo,
      onSignUpSuccess,
      onNext,
      setSearchParams,
      trackAccountDetailsSubmitted,
      trackSignupMethodSelected,
      history,
    ],
  )

  const passwordRequirements = [
    {
      validator: (value: string) => value.length >= 8,
      message: '8 characters',
    },
    {
      validator: (value: string) => /\d/.test(value),
      message: 'One number',
    },
    {
      validator: (value: string) => /[A-Z]/.test(value),
      message: 'One capital letter',
    },
  ]

  return (
    <MultiStepStepLayout
      title="Create your Autopilot account"
      centerContent
      scrollContainerClassName={Styles.scrollContainer}
    >
      <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit} noValidate className={Styles.form}>
            <div className={Styles.logo}>
              <WhiteLabelLogo workspace={invitedWorkspace} width={108} height={30} />
            </div>
            <div className={Styles.fields}>
              <div className={Styles.fieldGroup}>
                <label htmlFor="name">
                  <Text variant="smallList" color="var(--dark-80)">
                    Full Name
                    <span className={Styles.required}>*</span>
                  </Text>
                </label>
                <Input
                  id="name"
                  name="name"
                  autoFocus
                  placeholder="John Doe"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="name"
                  size="xl"
                />
                {touched.name && errors.name && (
                  <div className={Styles.error}>
                    <FormErrorMessage className={Styles.errorMessage}>{errors.name}</FormErrorMessage>
                  </div>
                )}
              </div>
              <div className={Styles.fieldGroup}>
                <label htmlFor="password">
                  <Text variant="smallList" color="var(--dark-80)">
                    Password
                    <span className={Styles.required}>*</span>
                  </Text>
                </label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Enter Password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="new-password"
                  size="xl"
                />
                {touched.password && errors.password && (
                  <div className={Styles.error}>
                    <FormErrorMessage className={Styles.errorMessage}>{errors.password}</FormErrorMessage>
                  </div>
                )}
              </div>
              <div
                className={cx(Styles.passwordValidationContainer, {
                  [Styles.passwordValidationContainerError]: touched.password && !!errors.password,
                })}
              >
                <Text variant="label" color="var(--dark-80)">
                  Password must contain at least:
                </Text>
                <div className={Styles.requirementOptions}>
                  {passwordRequirements.map(({ validator, message }, idx) => (
                    <div className={Styles.passwordRequirement} key={`validator-${idx}`}>
                      {validator(values.password) ? (
                        <Check className={Styles.success} size={14} color="var(--green-50)" />
                      ) : (
                        <Close size={14} color="var(--dark-80)" />
                      )}
                      <Text variant="metadata">{message}</Text>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {apiError && (
              <div className={Styles.error}>
                <FormErrorMessage className={Styles.errorMessage}>{apiError}</FormErrorMessage>
              </div>
            )}
            <Button type="submit" variant="primary" fullWidth size="lg" isDisabled={isSubmitting}>
              {isSubmitting ? 'Creating account...' : 'Continue'}
            </Button>
          </form>
        )}
      </Formik>
    </MultiStepStepLayout>
  )
}

export default CreateAccount
